import { z } from "zod";

/**
 * Therapist Code lookup — ASAP Flow 4C ★, Scheduled Flow 6C. ASAP-06 / OQ-11.
 *
 * The client types a code; the server says whether it names a therapist and,
 * if so, who — by FIRST NAME ONLY. Nothing else about the therapist crosses
 * this boundary: no last name, phone, email, hub, address, or staff id.
 *
 * Normalisation (casing, separators) is `normalizeTherapistCode` in
 * `@bednbody/logic`; both sides use it so a lookup and a booking carry the
 * same string.
 */
/**
 * The code itself. Shared with the booking contracts (`capacity.ts`), which
 * carry the CODE and never a staff id: the client is never given an id it could
 * put in a request body, so a Therapist Code booking has to be resolved
 * server-side (SECURITY §9a). One definition means the lookup and the booking
 * cannot disagree about what a well-formed code looks like.
 */
export const therapistCodeSchema = z
  .string()
  .trim()
  .toUpperCase()
  // MT-LEON01 (0336), or a legacy 6-character code. The legacy shape is kept
  // for good: test accounts are skipped by the reissue (0337) and keep theirs,
  // and a replaced therapist's old code still resolves during its grace period.
  // The client sends the normalised string (hyphen restored), so the pattern is
  // strict about the hyphen.
  .regex(
    /^(MT-[A-Z]{1,4}[0-9]{2}|[A-HJ-NP-Z2-9]{6})$/,
    "A Therapist Code looks like MT-LEON01.",
  );

export const therapistCodeLookupSchema = z.object({
  code: therapistCodeSchema,
});

export type TherapistCodeLookupInput = z.infer<typeof therapistCodeLookupSchema>;

/**
 * The lookup result.
 *
 * `schedulable` is `staff.active` — the ONLY approved availability signal today
 * (OQ-04: `active = false` means unschedulable). The Flow 4C trichotomy
 * (Available / Engaged / Unavailable) is a booking-engine verdict that depends
 * on the service address, window and live commitments; it is produced by
 * ASAP-01, not by this lookup, and is deliberately absent here rather than
 * approximated.
 */
export const therapistCodeLookupResultSchema = z
  .object({
    found: z.boolean(),
    /** Present only when `found` — first name, never a full name. */
    firstName: z.string().nullable(),
    schedulable: z.boolean().nullable(),
  })
  .strict();

export type TherapistCodeLookupResult = z.infer<typeof therapistCodeLookupResultSchema>;
